/**
 * 실행계획 옵션 — 실행계획 탭의 단추 줄(2026-10-26 사용자 결정: SQL 실행계획 분석 메뉴를 없애고 편집기 실행계획(Ctrl+E)에 합쳤다).
 *
 * 고른 값은 「실행」 설정(execution)에 저장되고 **다음 Ctrl+E 부터** 따른다. 「다시 분석」 은 이 탭의 SQL 을 지금 옵션으로 다시 뜬다.
 *  · 종류 — 예상(EXPLAIN PLAN, 실행하지 않음) / 실제(실행한 뒤 줄별 실측 — SELECT 가 아니면 데이터가 바뀔 수 있다)
 *  · 형식 — 예상은 DBMS_XPLAN.DISPLAY, 실제는 DISPLAY_CURSOR 의 format
 *  · 실측 수집 방식 · SQL Monitor 리포트(Tuning Pack) — 실제일 때만
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiSettings, FiChevronDown } from 'react-icons/fi';
import { PLAN_FORMATS, ACTUAL_PLAN_FORMATS, PLAN_STATS_METHODS } from '../utils/appSettings';
import { toCssPx } from '../utils/appZoom';
import './PlanOptionsMenu.css';

export default function PlanOptionsMenu({ options, onChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const popRef = useRef(null);
  // 팝오버는 그 문서의 body 에 고정 위치로 그린다 — 결과 영역이 낮으면 칸 안에 그릴 때 아래가 잘렸다(2026-10-26 화면 확인).
  // 아래 자리가 모자라면 위로 편다. 분리한 결과 창이면 그 창의 문서에 그린다
  const [pos, setPos] = useState(null);
  const actualMode = options && options.planMode === 'actual';
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    const btn = boxRef.current;
    const pop = popRef.current;
    if (!btn || !pop) return;
    const win = btn.ownerDocument.defaultView || window;
    const r = btn.getBoundingClientRect();
    const h = pop.getBoundingClientRect().height;
    const below = win.innerHeight - r.bottom;
    const up = h + 8 > below && r.top > below;
    setPos({
      right: toCssPx(Math.max(4, win.innerWidth - r.right)),
      ...(up ? { bottom: toCssPx(win.innerHeight - r.top + 4) } : { top: toCssPx(r.bottom + 4) }),
    });
  }, [open, actualMode]);
  useEffect(() => {
    if (!open) return undefined;
    const doc = boxRef.current ? boxRef.current.ownerDocument : document;
    const win = doc.defaultView || window;
    const inside = (n) => (boxRef.current && boxRef.current.contains(n)) || (popRef.current && popRef.current.contains(n));
    const onDown = (e) => { if (!inside(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onResize = () => setOpen(false);
    doc.addEventListener('mousedown', onDown);
    doc.addEventListener('keydown', onKey);
    win.addEventListener('resize', onResize);
    return () => { doc.removeEventListener('mousedown', onDown); doc.removeEventListener('keydown', onKey); win.removeEventListener('resize', onResize); };
  }, [open]);

  const o = options || {};
  const actual = o.planMode === 'actual';
  const fmt = actual ? (o.planActualFormat || 'ALLSTATS LAST') : (o.planFormat || 'TYPICAL');
  const set = (patch) => onChange({ ...o, ...patch });

  return (
    <div className="plo" ref={boxRef}>
      <button type="button" className={`plo-btn${open ? ' on' : ''}`} onClick={() => setOpen(v => !v)} aria-expanded={open}
        title={t('common:plo.hint')}>
        <FiSettings size={12} /> {t(actual ? 'common:plo.actual' : 'common:plo.estimated')} · {fmt} <FiChevronDown size={11} />
      </button>
      {open && createPortal((
        <div className="plo-pop" ref={popRef} role="dialog" aria-label={t('common:plo.title')}
          style={pos || { top: 0, right: 0, visibility: 'hidden' }}>
          <div className="plo-row">
            <span className="plo-label">{t('common:plo.kind')}</span>
            <div className="plo-seg" role="radiogroup">
              {['estimated', 'actual'].map(m => (
                <button key={m} type="button" role="radio" aria-checked={o.planMode === m || (m === 'estimated' && !actual)}
                  className={(m === 'actual') === actual ? 'on' : ''} onClick={() => set({ planMode: m })}>
                  {t(`common:plo.${m}`)}
                </button>
              ))}
            </div>
          </div>
          <div className="plo-note">{t(actual ? 'common:plo.actualNote' : 'common:plo.estimatedNote')}</div>
          <label className="plo-row">
            <span className="plo-label">{t('common:plo.format')}</span>
            {actual ? (
              <select value={fmt} onChange={e => set({ planActualFormat: e.target.value })}>
                {ACTUAL_PLAN_FORMATS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            ) : (
              <select value={fmt} onChange={e => set({ planFormat: e.target.value })}>
                {PLAN_FORMATS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            )}
          </label>
          {actual && (
            <>
              <label className="plo-row">
                <span className="plo-label">{t('common:plo.stats')}</span>
                <select value={o.planStatsMethod || 'session'} onChange={e => set({ planStatsMethod: e.target.value })}>
                  {PLAN_STATS_METHODS.map(m => <option key={m} value={m}>{t(`common:plo.statsMethod.${m}`)}</option>)}
                </select>
              </label>
              <label className="plo-row plo-check" title={t('common:tun.monitorReportHint')}>
                <input type="checkbox" checked={!!o.planMonitorReport} onChange={e => set({ planMonitorReport: e.target.checked })} />
                {t('common:tun.monitorReportOpt')}
              </label>
            </>
          )}
          <div className="plo-foot">{t('common:plo.applies')}</div>
        </div>
      ), boxRef.current ? boxRef.current.ownerDocument.body : document.body)}
    </div>
  );
}

PlanOptionsMenu.propTypes = {
  /** 「실행」 설정 — planMode · planFormat · planActualFormat · planStatsMethod · planMonitorReport */
  options: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};
